import React, { useState, useMemo } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';

const ParticipantTable = ({ 
  participants = [],
  onEdit,
  onDelete,
  onStatusChange,
  showActions = true,
  showFilters = true,
  columns = ['name', 'phone', 'status', 'payment', 'registeredAt']
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'registeredAt', direction: 'desc' });

  const statusOptions = [
    { value: 'all', label: '전체 상태' },
    { value: 'confirmed', label: '확정' },
    { value: 'pending', label: '대기' },
    { value: 'cancelled', label: '취소' }
  ];

  const paymentOptions = [
    { value: 'all', label: '전체 결제' },
    { value: 'paid', label: '완료' },
    { value: 'pending', label: '대기' },
    { value: 'overdue', label: '미납' }
  ];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'confirmed':
        return { color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle' };
      case 'pending':
        return { color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' };
      case 'cancelled':
        return { color: 'text-error', bg: 'bg-error/10', icon: 'XCircle' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Circle' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return '확정';
      case 'pending':
        return '대기';
      case 'cancelled':
        return '취소';
      default:
        return status || '-';
    }
  };

  const getPaymentConfig = (payment) => {
    switch (payment) {
      case 'paid':
        return { color: 'text-success', bg: 'bg-success/10', icon: 'DollarSign' };
      case 'pending':
        return { color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' };
      case 'overdue':
        return { color: 'text-error', bg: 'bg-error/10', icon: 'AlertTriangle' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted', icon: 'DollarSign' };
    }
  };

  const getPaymentLabel = (payment) => {
    switch (payment) {
      case 'paid':
        return '완료';
      case 'pending':
        return '대기';
      case 'overdue':
        return '미납';
      default:
        return payment || '-';
    }
  };

  const getMemberLabel = (isMember) => {
    return isMember ? '회원' : '게스트';
  };

  const filteredAndSortedParticipants = useMemo(() => {
    let filtered = participants?.filter(participant => {
      const matchesSearch = 
        participant?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        participant?.phone?.includes(searchQuery);
      
      const matchesStatus = statusFilter === 'all' || participant?.status === statusFilter;
      const matchesPayment = paymentFilter === 'all' || participant?.payment === paymentFilter;

      return matchesSearch && matchesStatus && matchesPayment;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        const aValue = a?.[sortConfig?.key];
        const bValue = b?.[sortConfig?.key];

        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [participants, searchQuery, statusFilter, paymentFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditClick = (participant) => {
    if (onEdit) {
      onEdit(participant);
    }
  };

  const handleDeleteClick = (participant) => {
    if (onDelete) {
      onDelete(participant);
    }
  };

  const handleStatusChangeClick = (participant, newStatus) => {
    if (onStatusChange) {
      onStatusChange(participant, newStatus);
    }
  };

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="이름/전화번호로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
          </div>
          
          <div className="flex gap-4">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="상태 필터"
              className="w-48"
            />
            
            <Select
              options={paymentOptions}
              value={paymentFilter}
              onChange={setPaymentFilter}
              placeholder="결제 필터"
              className="w-48"
            />
          </div>
        </div>
      )}
      <div className="hidden lg:block overflow-x-auto scrollbar-custom">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border">
              {columns?.includes('name') && (
                <th className="text-left p-4">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                  >
                    이름
                    <Icon 
                      name={sortConfig?.key === 'name' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  </button>
                </th>
              )}
              
              {columns?.includes('phone') && (
                <th className="text-left p-4">
                  <span className="font-heading font-medium text-foreground">전화번호</span>
                </th>
              )}
              
              {columns?.includes('status') && (
                <th className="text-left p-4">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                  >
                    상태
                    <Icon 
                      name={sortConfig?.key === 'status' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  </button>
                </th>
              )}
              
              {columns?.includes('payment') && (
                <th className="text-left p-4">
                  <button
                    onClick={() => handleSort('payment')}
                    className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                  >
                    결제
                    <Icon 
                      name={sortConfig?.key === 'payment' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  </button>
                </th>
              )}

              {columns?.includes('member') && (
                <th className="text-left p-4">
                  <span className="font-heading font-medium text-foreground">회원</span>
                </th>
              )}
              
              {columns?.includes('registeredAt') && (
                <th className="text-left p-4">
                  <button
                    onClick={() => handleSort('registeredAt')}
                    className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                  >
                    신청일
                    <Icon 
                      name={sortConfig?.key === 'registeredAt' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  </button>
                </th>
              )}
              
              {showActions && (
                <th className="text-right p-4">
                  <span className="font-heading font-medium text-foreground">관리</span>
                </th>
              )}
            </tr>
          </thead>
          
          <tbody>
            {filteredAndSortedParticipants?.map((participant, index) => {
              const statusConfig = getStatusConfig(participant?.status);
              const paymentConfig = getPaymentConfig(participant?.payment);
              
              return (
                <tr 
                  key={participant?.id || index}
                  className="border-b border-border hover:bg-muted/50 transition-athletic"
                >
                  {columns?.includes('name') && (
                    <td className="p-4">
                      <div className="font-medium text-foreground">{participant?.name}</div>
                      {participant?.email && (
                        <div className="text-sm caption text-muted-foreground">{participant?.email}</div>
                      )}
                    </td>
                  )}
                  {columns?.includes('phone') && (
                    <td className="p-4">
                      <span className="data-text text-foreground">{participant?.phone}</span>
                    </td>
                  )}
                  {columns?.includes('status') && (
                    <td className="p-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${statusConfig?.bg}`}>
                        <Icon name={statusConfig?.icon} size={14} color={`var(--color-${participant?.status === 'confirmed' ? 'success' : participant?.status === 'pending' ? 'warning' : 'error'})`} />
                        <span className={`text-sm font-medium ${statusConfig?.color}`}>
                          {getStatusLabel(participant?.status)}
                        </span>
                      </div>
                    </td>
                  )}
                  {columns?.includes('payment') && (
                    <td className="p-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${paymentConfig?.bg}`}>
                        <Icon name={paymentConfig?.icon} size={14} color={`var(--color-${participant?.payment === 'paid' ? 'success' : participant?.payment === 'pending' ? 'warning' : 'error'})`} />
                        <span className={`text-sm font-medium ${paymentConfig?.color}`}>
                          {getPaymentLabel(participant?.payment)}
                        </span>
                      </div>
                    </td>
                  )}

                  {columns?.includes('member') && (
                    <td className="p-4">
                      <span className="text-sm font-medium text-foreground">{getMemberLabel(Boolean(participant?.isMember))}</span>
                    </td>
                  )}
                  {columns?.includes('registeredAt') && (
                    <td className="p-4">
                      <span className="text-sm caption text-muted-foreground">
                        {formatDate(participant?.registeredAt)}
                      </span>
                    </td>
                  )}
                  {showActions && (
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit"
                          onClick={() => handleEditClick(participant)}
                        >
                          수정
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDeleteClick(participant)}
                        >
                          삭제
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredAndSortedParticipants?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">참가자가 없습니다</p>
          </div>
        )}
      </div>
      <div className="lg:hidden">
        <div className="bg-card border border-border rounded-md divide-y divide-border">
          {filteredAndSortedParticipants?.map((participant, index) => {
            const statusConfig = getStatusConfig(participant?.status);
            const paymentConfig = getPaymentConfig(participant?.payment);

            return (
              <div
                key={participant?.id || index}
                className="p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-medium text-foreground truncate">
                      {participant?.name}
                    </p>
                    {columns?.includes('phone') && (
                      <p className="text-xs data-text text-muted-foreground mt-0.5">
                        {participant?.phone}
                      </p>
                    )}
                    {columns?.includes('member') && (
                      <p className="text-xs caption text-muted-foreground mt-0.5">
                        {getMemberLabel(Boolean(participant?.isMember))}
                      </p>
                    )}
                    {columns?.includes('registeredAt') && participant?.registeredAt && (
                      <p className="text-xs caption text-muted-foreground mt-0.5">
                        신청: {formatDate(participant?.registeredAt)}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${statusConfig?.bg}`}>
                      <Icon name={statusConfig?.icon} size={12} />
                      <span className={`text-xs font-medium ${statusConfig?.color}`}>
                        {getStatusLabel(participant?.status)}
                      </span>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${paymentConfig?.bg}`}>
                      <Icon name={paymentConfig?.icon} size={12} />
                      <span className={`text-xs font-medium ${paymentConfig?.color}`}>
                        {getPaymentLabel(participant?.payment)}
                      </span>
                    </div>
                  </div>
                </div>

                {showActions && (
                  <div className="flex gap-2 pt-2 mt-2 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Edit"
                      iconPosition="left"
                      onClick={() => handleEditClick(participant)}
                      fullWidth
                    >
                      수정
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => handleDeleteClick(participant)}
                      fullWidth
                    >
                      삭제
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

          {filteredAndSortedParticipants?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">참가자가 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantTable;